import React, { useEffect, useRef, useState } from "react";
import { Button, Grid, Input, Message } from "@arco-design/web-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import Content from "../Layout/Content";
import LoadingState from "../Layout/LoadingState";
import SummaryCard from "../Layout/SummaryCard";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import { FetchApi, PostApi, OverwriteApi } from "../Api/AuthenticatedApi";
import EmailComposer from "../Editor/EmailComposer/EmailComposer";
import RecordNotFound from "../Components/RecordNotFound";

const { Row, Col } = Grid;

// Create/edit an email template using the shared <EmailComposer> (same editor as
// broadcasts/campaigns/sequences). A template is just name + subject/html/text +
// Easy Email design.
export default function TemplateEditor() {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const isNew = templateId === "new";

    const [loading, setLoading] = useState(!isNew);
    const [loadFailed, setLoadFailed] = useState(false);
    const [name, setName] = useState("");

    const composerRef = useRef(null);
    const [composerSeed, setComposerSeed] = useState({});
    const [seedKey, setSeedKey] = useState(0);

    const authFail = () => navigate("/login");
    const backToList = () => navigate("/templates");

    useEffect(() => {
        if (isNew) return; // blank composer via lazy-init; nothing to fetch
        FetchApi(getAccessTokenSilently, `/api/email-templates/${templateId}`,
            (data) => {
                setName("" + (data.name || ""));
                setComposerSeed({
                    subject: data.mail_subject_template,
                    html: data.mail_html_template,
                    text: data.mail_text_template,
                    design: data.editor_design,
                });
                setSeedKey((k) => k + 1);
                setLoading(false);
            },
            authFail,
            () => { setLoading(false); setLoadFailed(true); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, getAccessTokenSilently]);

    const save = () => {
        if (!name.trim()) {
            Message.error("Template name is required");
            return;
        }
        const content = composerRef.current ? composerRef.current.getContent() : {};
        const payload = { name: name.trim(), ...content };
        if (isNew) {
            PostApi(getAccessTokenSilently, "/api/email-templates/",
                () => { Message.success("Template created"); backToList(); }, authFail,
                (err) => Message.error((err && err.message) || "Failed to create template"), payload);
        } else {
            OverwriteApi(getAccessTokenSilently, `/api/email-templates/${templateId}`,
                () => { Message.success("Template saved"); backToList(); }, authFail,
                (err) => Message.error((err && err.message) || "Failed to save template"), payload);
        }
    };

    if (loadFailed) {
        return <RecordNotFound what="template" backTo="/templates" backLabel="Back to Templates" />;
    }
    if (loading) {
        return <LoadingState />;
    }

    return (
        <Content>
            <SimpleBreadcrumbs
                elements={[
                    { name: "Templates", path: "/templates" },
                    { name: isNew ? "New template" : (name || "Template") },
                ]}
            />

            <SummaryCard title={isNew ? "New template" : "Edit template"}>
                <Row gutter={16}>
                    <Col span={24} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Template name</div>
                        <Input style={{ maxWidth: 400 }} value={name} placeholder="e.g. Monthly newsletter" onChange={setName} />
                    </Col>

                    <Col span={24}>
                        <EmailComposer
                            ref={composerRef}
                            initial={composerSeed}
                            seedKey={seedKey}
                            enablePersonalize
                        />
                    </Col>

                    <Col span={24} style={{ marginTop: 16 }}>
                        <Button type="primary" onClick={save} style={{ marginRight: 8 }}>
                            {isNew ? "Create template" : "Save template"}
                        </Button>
                        <Button onClick={backToList}>Cancel</Button>
                    </Col>
                </Row>
            </SummaryCard>
        </Content>
    );
}
